import { OTHER_COLONY } from '@/components/blocks/AddressForm/constants';
import { AddressErrors } from '@/components/blocks/AddressForm/types';
import { isNonEmpty, notSelectOption, toStr } from '@/components/blocks/AddressForm/utils';
import { AddressFormActionLabels, EditableAddress } from '@/data/types/Address';
import { FormState } from '@/utils/useForm';
import { useMemo } from 'react';
import {
	ADDRESS_FORM_SAVE,
	CHECKOUT_STEP2_ADDRESS,
} from '@/components/content/CheckOutV2/constants';

type UseAddressFormActionsProps = {
	form: FormState<EditableAddress>;
	onCancel?: () => void;
	submitLabel: AddressFormActionLabels;
	cancelLabel?: AddressFormActionLabels;
	addressErrors: AddressErrors;
	showInputStateAndCity: boolean;
	isEdited: boolean;
	isDisabled?: boolean;
	isLoading?: boolean;
	hiddenNicknameField: boolean;
	addressType?: 'Billing';
	isCheckout?: boolean;
	isNotBilling?: boolean;
	formId?: string;
};

export const useAddressFormActions = ({
	form,
	onCancel,
	submitLabel,
	cancelLabel,
	addressErrors,
	showInputStateAndCity,
	isEdited,
	isDisabled,
	isLoading,
	hiddenNicknameField,
	addressType,
	isCheckout = false,
	isNotBilling = false,
	formId,
}: UseAddressFormActionsProps) => {
	const { submitting, values } = form;

	const isValid = useMemo(() => {
		const nickName = isValidNickName({
			values,
			hiddenNicknameField,
			addressErrors,
		});
		const street = isNonEmpty(values.addressLine1);
		const exteriorNumber = isNonEmpty(values.addressLine2);
		const zipCode = toStr(values.zipCode).length === 5;
		const { colony, colonyNotFound } = getColonyNotFound({
			values,
			showInputStateAndCity,
		});
		const municipality = isValidMunicipality({ values });
		const state = isNonEmpty(values.state);
		const firstName = isValidFirstName({ values, addressErrors, addressType });
		const lastName = isValidLastName({ values, addressErrors, addressType });

		return (
			nickName &&
			street &&
			exteriorNumber &&
			zipCode &&
			colony &&
			colonyNotFound &&
			municipality &&
			state &&
			firstName &&
			lastName
		);
	}, [values, hiddenNicknameField, addressErrors, showInputStateAndCity, addressType]);

	const isSubmitDisabled = getIsSubmitDisabled({
		isNotBilling,
		isValid,
		isEdited,
		isDisabled,
		isLoading,
		submitting,
	});

	const buttonId = isCheckout ? CHECKOUT_STEP2_ADDRESS : ADDRESS_FORM_SAVE;

	return {
		// Core props
		onCancel,
		submitLabel,
		cancelLabel,
		submitting,

		// Custom props
		isLoading,
		isSubmitDisabled,
		buttonId,
		formId,
	};
};

const getColonyNotFound = ({
	values,
	showInputStateAndCity,
}: {
	values: EditableAddress;
	showInputStateAndCity: boolean;
}) => {
	let colony = true;
	let colonyNotFound = true;

	if (showInputStateAndCity) {
		colonyNotFound = isNonEmpty(values.addressLine3Txt);
	} else {
		colony = isNonEmpty(values.addressLine3) && notSelectOption(values.addressLine3);
		colonyNotFound =
			values.addressLine3 === OTHER_COLONY ? isNonEmpty(values.addressLine3Txt) : true;
	}

	return {
		colony,
		colonyNotFound,
	};
};

const isValidNickName = ({
	values,
	hiddenNicknameField,
	addressErrors,
}: {
	values: EditableAddress;
	hiddenNicknameField: boolean;
	addressErrors: AddressErrors;
}) => {
	const nickName = !hiddenNicknameField
		? isNonEmpty(values.nickName) && !addressErrors.nickName
		: true;

	return nickName;
};

const isValidMunicipality = ({ values }: { values: EditableAddress }) =>
	isNonEmpty(values.city) && notSelectOption(values.city);

const isValidFirstName = ({
	values,
	addressErrors,
	addressType,
}: {
	values: EditableAddress;
	addressErrors: AddressErrors;
	addressType?: 'Billing';
}) => (addressType === 'Billing' ? isNonEmpty(values.firstName) && !addressErrors.firstName : true);

const isValidLastName = ({
	values,
	addressErrors,
	addressType,
}: {
	values: EditableAddress;
	addressErrors: AddressErrors;
	addressType?: 'Billing';
}) => (addressType === 'Billing' ? isNonEmpty(values.lastName) && !addressErrors.lastName : true);

const getIsSubmitDisabled = ({
	isNotBilling,
	isValid,
	isEdited,
	isDisabled,
	isLoading,
	submitting,
}: {
	isNotBilling: boolean;
	isValid: boolean;
	isEdited: boolean;
	isDisabled?: boolean;
	isLoading?: boolean;
	submitting?: boolean;
}) => {
	if (isNotBilling) {
		return submitting || isDisabled || isLoading;
	}

	return !(isValid && isEdited) || submitting || isDisabled || isLoading;
};
